import { h } from 'preact';
import Store from 'store/store';
import * as actions from 'store/actions';
import { oneTrust } from 'utils/gdpr';
import { evidonCategories } from 'utils/gdpr/evidon/categories';
import * as cookieCategories from 'utils/gdpr/categories';
import * as cookieConstants from 'utils/gdpr/constants';
import {
	calculateShowtimesStatus,
	getSize,
	getPageTitle,
	getPath,
	rawSearchLocation,
	getGeoFromIPAsync,
} from 'utils/clientUtils';
import {
	track,
	initTracker,
	gaCustom,
	updateDataLayer,
	updateDataLayerPageView
} from 'utils/tracking';
import { debounce, accessNested, parseDate, getStylingImagePath, encodeHtmlRecursively } from 'utils/utils';
import { smoothScrollTo } from 'utils/smoothScroll';
import queryString from 'qs';
import route from 'components/core/link/route';
import 'store/updateExtra';
import geoFencing from 'utils/geoFencing';
import CookiesModal from 'components/shared/cookiesModal/cookiesModal';
import { displayRegionModalIfNeeded } from 'utils/regionModal';
import Geo from 'models/Geo';

// NOTE: SHOWTIMES EVENTS NOT INCLUDED IN THIS

/** Listen to event triggers and update the store in reaction */


// =~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=
// Base events

/**
 * Will be triggered every time an event is fired.
 * NOTE: This should be in store.js
 * @param {string} reactionName - the name of the event
 * @param {*} data - the first argument when the event was called
 */
// Store.on('afterAll', (reactionName, data) => {
// 	if (reactionName !== 'update') {
// 		console.debug('TRIGGERED:', reactionName);
// 	}
// });

/**
 * Update something in the store.
 * Should be used with care! to update anything irregular in the store.
 * NOTE: This is only used by the Warner Bros DE pixels to set movie genres.
 *       I think It should be a more explicit event like "UPDATE_GENRES"
 * @param {Object} storeData - the changes to apply on the store
 */
Store.on(actions.UPDATE_STORE, (storeData) => {
	const store = Store.get();
	store.set(storeData);
});


// =~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=
// Loader events

/**
 * Update loader data.
 * This is only used in Doctor Strange AFAIK.
 * @param {Object} loader - the loader data
 */
Store.on(actions.UPDATE_LOADER, (loader) => {
	const store = Store.get();

	if (Object.prototype.hasOwnProperty.call(store, 'loader')) {
		store.loader.set(loader);
	} else {
		store.set({ loader: loader });
	}
});


// =~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=
// Page interactions

/**
 * Update the active page.
 * This event is fired by the "PAGE_STATE_LOADED", which will automatically detect the active
 * page based on the page url.
 * @param {Object} activePage - the active page object
 */
Store.on(actions.SET_ACTIVE_PAGE, (activePage) => {
	if (!activePage) return;
	// console.log('Navigating to', activePage.path);
	gaCustom('set', 'page', activePage.path);
	gaCustom('send', 'pageview');
	trackPageView(activePage.id, null, false);

	const state = Store.get();
	const { copy } = state.appData;

	if (activePage.data && state.background && state.background.videoEnabled !== activePage.data.allowBgVideo) {
		// Update the allowBgVideo
		const { data } = activePage;
		Store.emit(actions.SET_BACKGROUND_VIDEO_ENABLED, data && data.allowBgVideo);
	}

	// Close the sidenav
	Store.emit(actions.SET_SIDE_NAV_OPEN, false);

	const sideNavBelow = accessNested(activePage, 'data.header.breakpoint') ||
		accessNested(state, 'appData.header.breakpoint');
	if (typeof window !== 'undefined' && state.activePage.id !== activePage.id && !activePage.order) {
		// Scroll to top ?
		// (will change the scroll if the new page is smaller anyway)
		// If we keep that, add delay so that some pages can save their leaving state scroll position
		const noScroll = accessNested(state, 'appData.options.noScroll', false);
		const noSmoothScrollTopTransitionDuration = accessNested(state, 'activePage.data', 0);
		const customScrollDuration = accessNested(state, 'appData.styling.variables.customScrollDuration', 200);
		if (!noScroll) {
			const scrollDuration = customScrollDuration || 200;
			setTimeout(() => smoothScrollTo('root', 0, scrollDuration, { force: true }), noSmoothScrollTopTransitionDuration);
		}
	}

	// Set the document title to the active page one
	document.title = getPageTitle(activePage, state.appData);

	///  META DESCRIPTION
	const desc = document.querySelector('meta[name="description"]');
	if (desc && typeof desc === 'object') {
		desc.content = copy['$' + activePage.id.toUpperCase() + '_META_DESCRIPTION'] || copy.$META_DESCRIPTION;
	}

	const ogDesc = document.querySelector('meta[property="og:description"]');
	if (ogDesc && typeof ogDesc === 'object') {
		ogDesc.content = copy.$META_OGDESCRIPTION || copy['$' + activePage.id.toUpperCase() + '_META_DESCRIPTION'] || copy.$META_DESCRIPTION;
	}

	const twitterDesc = document.querySelector('meta[name="twitter:description"]');
	if (twitterDesc && typeof twitterDesc === 'object') {
		twitterDesc.content = copy.$META_OGDESCRIPTION || copy['$' + activePage.id.toUpperCase() + '_META_DESCRIPTION'] || copy.$META_DESCRIPTION;
	}


	///  META TITLE
	const ogMetaTitle = document.querySelector('meta[property="og:title"]');
	if (ogMetaTitle && typeof ogMetaTitle === 'object') {
		ogMetaTitle.content = copy.$META_OGTITLE || copy['$' + activePage.id.toUpperCase() + '_META_TITLE'] || copy.$META_TITLE;
	}


	const twitterMetaTitle = document.querySelector('meta[name="twitter:title"]');
	if (twitterMetaTitle && typeof twitterMetaTitle === 'object') {
		twitterMetaTitle.content = copy['$' + activePage.id.toUpperCase() + '_META_TITLE'] || copy.$META_TITLE;
	}

	/// META IMAGE
	const imageRegion     = state.appData.urls.roots.imageRegion;
	const defaultShareImage = copy['$' + activePage.id.toUpperCase() + '_META_IMAGE'] || copy.$META_IMAGE || state.appData.urls.shareImage || (imageRegion + '/200x200.png');
	const twitterShareImage = copy['$' + activePage.id.toUpperCase() + '_META_TWITTER_IMAGE'] || accessNested(state, 'appData.meta.social.twitter.customShareImage');

	let pageShareImage = accessNested(activePage, 'data.shareImage');
	let shareImage = pageShareImage || getStylingImagePath(state.appData, '200x200', defaultShareImage);

	if (shareImage.startsWith('//')) {
		shareImage = 'https:' + shareImage;
	}

	const ogMetaImage = document.querySelector('meta[property="og:image"]');
	if (ogMetaImage && typeof ogMetaImage === 'object') {
		ogMetaImage.content = shareImage;
	}

	const twitterMetaImage = document.querySelector('meta[name="twitter:image"]');
	if (twitterMetaImage && typeof twitterMetaImage === 'object') {
		twitterMetaImage.content = twitterShareImage || shareImage;
	}

	/// URL
	const ogMetaImageSecureUrl = document.querySelector('meta[property="og:image:secure_url"]');
	if (ogMetaImageSecureUrl && typeof ogMetaImageSecureUrl=== 'object') {
		ogMetaImageSecureUrl.content = shareImage;
	}

	/// SITE NAME
	const ogMetaSiteName = document.querySelector('meta[property="og:site_name"]');
	if (ogMetaSiteName && typeof ogMetaSiteName === 'object') {
		ogMetaSiteName.content = copy.$META_OGSITENAME || getPageTitle(activePage, state.appData);
	}

	// ///////////

	// const header = state.header;
	Store.get().set({ activePage });

	// Have to re-get() since the reference is changed after the first set
	if (state.header && state.header.sideNavBelow !== sideNavBelow) {
		Store.get().header.set({ sideNavBelow });
		Store.emit(actions.SET_SIDE_NAV_ACTIVE, state.client.deviceSize);
	}

	Store.emit(actions.CLOSE_TICKETING);
});


// =~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=
// Background interactions

/**
 * Update any of the background settings.
 * @param {Object} values - object with new background settings
 */
function updateBackground(values) {
	const bg = Store.get().background;
	if (bg) {
		bg.set(values);
	}
}
Store.on(actions.UPDATE_BACKGROUND_SETTINGS, updateBackground);

/**
 * Set whether the background video should play.
 * @param {boolean} videoPlaying - whether the background video is playing
 */
Store.on(actions.SET_BACKGROUND_VIDEO_PLAYING, (videoPlaying) => {
	updateBackground({ videoPlaying });
});

/**
 * Set whether the video background is enabled.
 * @param {boolean} videoEnabled - true to enable the video background
 */
Store.on(actions.SET_BACKGROUND_VIDEO_ENABLED, (videoEnabled) => {
	updateBackground({ videoEnabled });
});

/**
 * Mute or unmute the background video.
 * @param {boolean} muted - true to mute the video, false to unmute
 */
Store.on(actions.SET_BACKGROUND_VIDEO_MUTED, (muted) => {
	updateBackground({ muted });
});

/**
 * Set the currently displayed background DOM element
 * @param {HTMLElement} element - current DOM element being the background
 */
Store.on(actions.SET_BACKGROUND_ELEMENT, (element) => {
	updateBackground({ element });
});


// =~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=
// Header interactions

/**
 * Set whether the header menu is opened.
 * @param {boolean} sideNavOpen - true to open the menu, false to close it
 */
Store.on(actions.SET_SIDE_NAV_OPEN, (sideNavOpen) => {
	const header = Store.get().header;
	if(!header) return;
	if (!!sideNavOpen === !!header.sideNavOpen) return;
	track({ a: sideNavOpen ? 'nav-open' : 'nav-closed' });
	header.set({ sideNavOpen });
});

/**
 * Set whether the header is hidden.
 * @param {boolean} hidden - true to hide the menu, false to show it
 */
Store.on(actions.HEADER_HIDDEN, (hidden) => {
	Store.get().header.set({ hidden });
});

/**
 * Set whether the side nav is active
 * @param {string} deviceSize - the current device size
 */
Store.on(actions.SET_SIDE_NAV_ACTIVE, (deviceSize) => {
	const store = Store.get();
	const headerStore = store.header;

	if (!headerStore) {
		return;
	}

	const { sideNavBelow } = headerStore;
	let sideNavActive = false;

	if (sideNavBelow === 'desktop' ||
		(sideNavBelow === 'tablet' && (deviceSize === 'tablet' || deviceSize === 'mobile')) ||
		sideNavBelow === deviceSize) {
		sideNavActive = true;
	}

	const device = store.client.device;
	const isTablet = device === 'ipad' || (device === 'android' && deviceSize === 'tablet');
	if (accessNested(store, 'appData.header.sideNavOnTablet') && isTablet) {
		sideNavActive = true;
	}

	if (headerStore.hideHeader) {
		sideNavActive = true;
	}

	headerStore.set({ sideNavActive });
});


// =~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=
// Footer interactions

/**
 * Store the current size of the footer.
 * FIXME: Commented 3 months ago, can we remove the handler + event?
 * @param {number} size - the current size of the footer in pixels
 */
// Store.on(actions.FOOTER_SIZE, (size) => {
// 	Store.get().footer.set({ size });
// });


// =~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=
// Quizz interactions

/**
 * Set wheter a quiz is started.
 * @param {boolean} started - true to start the quizz
 * @param {string=} quizId - the page id of the quizz started, by default it's the active page
 */
Store.on(actions.QUIZ_STARTED, (started, quizId) => {
	const quiz = quizId ? Store.get().appData.pages[quizId] : Store.get().activePage;
	if (quiz) {
		quiz.set({ started });
	}
});

Store.on(actions.RIGHT_OR_WRONG_QUIZ_FINISHED, () => {
	Store.get().set({ quizFinished: true });
});

// =~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=
// Client interactions

/**
 * Update the stored client dimensions.
 * @param {Object} dimensions - the new client dimensions
 */
Store.on(actions.SET_CLIENT_SIZE, (dimensions) => {
	const store = Store.get();
	const clientStore = store.client;

	const deviceSize = getSize(dimensions);
	const orientation = dimensions.width > dimensions.height ? 'landscape' : 'portrait';

	if (deviceSize !== clientStore.deviceSize) {
		Store.emit(actions.SET_SIDE_NAV_ACTIVE, deviceSize);
	}

	clientStore.set({
		dimensions,
		deviceSize,
		orientation
	});
});

(() => {
	const validOrientations = ['portrait', 'landscape'];
	let refCount = 0;

	// NOTE: This system is not perfect: it is too permissive.
	//       Basically, you can aquire a lock for landscape while a lock on portrait is still active.
	//       Perhaps we could add a priority like 'portrait' > 'landscape'?

	/**
	 * Start forcing the orientation of the device.
	 * @param {string} orientation - the device orientation, either 'portrait' or 'landscape'
	 */
	Store.on(actions.START_FORCE_CLIENT_ORIENTATION, (orientation) => {
		if (validOrientations.indexOf(orientation) === -1) {
			console.error(`FORCE_ORIENTATION: unknown device orientation "${orientation}"`);
			return;
		}
		const uiState = Store.get().header.uiState;
		const { overlayOrientation } = uiState;
		const orientationChanged = orientation !== overlayOrientation;

		if (refCount > 0 && orientationChanged) {
			console.error(`FORCE_ORIENTATION: received a "${orientation}" orienation force request but ` +
				`there are still ${refCount} ongoing requests for "${overlayOrientation}". The behavior ` +
				'is now undefined.');
			refCount = 0; // accept the request anyway
		}

		refCount++;
		if (orientationChanged) {
			uiState.set({ overlayOrientation: orientation });
		}
	});

	/**
	 * Stop forcing the orientation of the device.
	 * Note that this function has effects only when it's called the same number of times
	 * the function to start forcing the orientation has been called.
	 * @param {boolean=} force - ignore the previous orientation requests and stop forcing it
	 */
	Store.on(actions.STOP_FORCE_CLIENT_ORIENTATION, (force) => {
		const uiState = Store.get().header.uiState;
		const { overlayOrientation } = uiState;

		if (force) refCount = 0;
		if (refCount > 0) refCount--;
		if (refCount === 0 && overlayOrientation) {
			uiState.set({ overlayOrientation: false });
		}
	});

})();

// =~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=
// Page load

function detectCurrentPage(state, url) {
	state = state || Store.get();
	url = (url || location.pathname);

	if (accessNested(state, 'appData.options.disableAutomaticPageDetection')) {
		return null;
	}

	if (location.hostname === 'localhost') {
		// to prevent pages with /preview/ path to automatically be selected on local builds
		url = url.replace('/preview','');
	}

	let bestMatch = null;
	let bestMatchCount = 0;
	const pages = state.appData.pages;
	Object.keys(pages).forEach(pageId => {
		const page = pages[pageId];
		let path = page.path.slice(0, -1);
		if (path === '') {
			path = '/';
		}
		if (url.indexOf(path) >= 0 && !page.baseHtml) {
			if (bestMatchCount < path.length) {
				bestMatch = page;
				bestMatchCount = path.length;
			}
		}
	});
	return bestMatch;
}

let storageEnabled = true;
let originalSetItem;
const setItemQueue = [];
let originalCookieDescriptor;
const cookieQueue = [];

const disableStorage = () => {
	if (!storageEnabled) return;
	storageEnabled = false;
	// Delay all potential localStorage.setItem && document.cookie = stuff to after the user has accepted
	// (not foolproof, only works for same-domain cookies not for things like youtube iframes etc.)
	try {
		if (!originalSetItem) {
			originalSetItem = localStorage.setItem;
		}
		localStorage.setItem = function() {
			console.warn('NOTE: LOCAL STORAGE TEMPORARILY DISABLED DUE TO USER CONSENT REQUIREMENT');
			setItemQueue.push([this, ...arguments]);
		};
	} catch (e) {
		console.error('Could not disable local storage:', e);
	}
	try {
		if (!originalCookieDescriptor) {
			originalCookieDescriptor = Object.getOwnPropertyDescriptor(Document.prototype, 'cookie') || Object.getOwnPropertyDescriptor(HTMLDocument.prototype, 'cookie');
			Object.defineProperty(document, 'cookie', {
				get: originalCookieDescriptor.get.bind(document),
				configurable: true
			});
		}
		Object.defineProperty(document, 'cookie', {
			set: value => {
				console.warn('NOTE: COOKIES TEMPORARILY DISABLED DUE TO USER CONSENT REQUIREMENT');
				cookieQueue.push(value);
			}
		});
	} catch (e) {
		console.error('Could not disable cookie storage:', e);
	}
};

const enableStorage = () => {
	if (storageEnabled) return;
	storageEnabled = true;
	try {
		localStorage.setItem = originalSetItem;
		setItemQueue.forEach(elem => localStorage.setItem.call(...elem));
		// Empty the array without changing the ref
		setItemQueue.length = 0;
	} catch (e) {
		console.error('Could not re-enable local storage:', e);
	}
	try {
		Object.defineProperty(document, 'cookie', {
			set: originalCookieDescriptor.set.bind(document)
		});
		cookieQueue.forEach(elem => document.cookie = elem);
		cookieQueue.length = 0;
	} catch (e) {
		console.error('Could not re-enable cookie storage:', e);
	}
};

Store.on(actions.DISABLE_STORAGE, disableStorage);
Store.on(actions.ENABLE_STORAGE, enableStorage);

Store.on(actions.SHOW_COOKIES_MODAL, (callback) => {
	disableStorage();
	Store.emit(actions.SHOW_MODAL, CookiesModal, (accept) => {
		window.preventCookies = !accept;
		Store.get().client.set({ cookies: accept });
		if (accept) {
			enableStorage();
		}
		if (typeof callback === 'function') {
			callback(accept);
		}
	}, {
		type: 'cookieModal',
		blockClose: true,
		backdropColor: 'rgba(0, 0, 0, .8)',
		fadeIn: 600,
		appData: Store.get().appData
	});
});


/**
 * Called when a new page is loaded.
 */
Store.on(actions.BEFORE_LOAD, () => {
	const state = Store.get().toJS();
	const appData = state.appData;

	const features = appData.features;
	let powsterCookiesEnabled = features?.cookiesModal;
	let tealiumCookiesEnabled = features?.enableTealiumCookieConsent;
	let evidonCookiesEnabled = features?.requireUserConsent;
	let ensightenCookiesEnabled = features?.enableEnsighten;
	let oneTrustCookiesEnabled = features?.oneTrustCookieConsent;
	let didomiCookiesEnabled = features?.didomiCookieConsent;
	let SMECookiesEnabled = features?.SMECookieConsent;
	let cookiesEnabled = powsterCookiesEnabled || tealiumCookiesEnabled || ensightenCookiesEnabled || evidonCookiesEnabled || oneTrustCookiesEnabled || didomiCookiesEnabled || SMECookiesEnabled;
	if (!cookiesEnabled) {
		const allCats = [...Object.keys(cookieConstants), 'UNCATEGORIZED'];
		cookieCategories.set(allCats);
	}

	if (appData.options.requireUserConsent) {
		Store.emit(actions.DISABLE_STORAGE);
		Store.emit(actions.DISABLE_PIXELS);

		if (window.evidon) {
			const originalConsentCallback = window.evidon.priorConsentCallback;
			window.evidon.priorConsentCallback = function() {
				Store.emit(actions.ENABLE_STORAGE);
				Store.emit(actions.ENABLE_PIXELS);
				const getConsentedCategories = accessNested(window, 'evidon.notice._getConsentedCategories', undefined);
				if (typeof getConsentedCategories === 'function') {
					const consentedCategories = getConsentedCategories();
					evidonCategories.set(Object.keys(consentedCategories).filter(k => consentedCategories[k]));
				}
				if (typeof originalConsentCallback === 'function') {
					originalConsentCallback(...arguments);
				}
			};

			window.evidon.consentWithdrawnCallback = () => {
				Store.emit(actions.DISABLE_STORAGE);
				Store.emit(actions.DISABLE_PIXELS);
			};

			if (typeof window.evidon.loadScripts === 'function') {
				window.evidon.loadScripts();
			}
		}
	}

	Store.emit(actions.SET_SIDE_NAV_ACTIVE, state.client.deviceSize);

	const detectedPage = detectCurrentPage(state);
	if (detectedPage && detectedPage.id !== state.activePage.id) {
		Store.emit(actions.SET_ACTIVE_PAGE, detectedPage);
		// Temp update it in our object to be able to work with it
		state.activePage = detectedPage;
	}

	const parsedQS = queryString.parse(location.search.slice(1));
	if (parsedQS.referrer) {
		window.customReferrer = parsedQS.referrer;
	}

	let showbarebones = parsedQS.showbarebones;
	const embedmode = parsedQS.embedmode;
	// if (embedmode) {
	// 	// enable showbarebones
	// 	showbarebones = true;
	// }
	if (appData.options.showbarebones) {
		window.showbarebones = true;
	}
	if (window.showbarebones !== undefined) showbarebones = window.showbarebones;
	if (showbarebones) {
		Store.get().header.set({
			hideHeader: true,
			sideNavActive: true
		});

		document.body.classList.add('bodyBarebones');
		if (accessNested(state, 'appData.pages.showtimes.data')) {
			Store.get().appData.pages.showtimes.data.set({
				forceFullHeader: true
			});
		}
	}

	if (parsedQS.iframe) {
		// Apply fixes specific to iframe bugs
		Store.get().client.set({
			iframe: true
		});

		// if (state.client.isIOS) {
		// 	// Due to iframe bugs on ios, let's enable the embedMode
		// 	embedmode = true;
		// }
	}

	if (embedmode) {
		// Add in global class to body
		document.body.classList.add('embedMode');
		// Update the store
		Store.get().client.set({
			embedMode: true
		});
	}

	const onUrlChange = (e) => {
		const state = Store.get();

		let activePage = null;
		if (e && e.state && Object.prototype.hasOwnProperty.call(e.state, 'pageId')) {
			activePage = state.appData.pages[e.state.pageId];
		} else {
			activePage = detectCurrentPage(state);
		}

		if (activePage && activePage.id !== accessNested(state, 'activePage.id')) {
			Store.emit(actions.SET_ACTIVE_PAGE, activePage);
		}

		Store.emit(actions.URL_CHANGED, e);
	};
	window.addEventListener('popstate', onUrlChange);
	history.onpushstate = onUrlChange;
	history.onreplacestate = onUrlChange;

	let showtimesStatus = false;
	if (state.appData.pages && state.appData.pages.showtimes) {
		const dates = accessNested(state, 'appData.meta.dates', {});
		const activeDate = parseDate(dates.live || dates.release || 0);
		showtimesStatus = calculateShowtimesStatus(activeDate, state.appData.options.forceLive, state.appData.pages);
	}
	Store.get().showtimes.set({ status: showtimesStatus }).now();

	let updateSize = () => Store.emit(actions.SET_CLIENT_SIZE, {
		width: window.innerWidth,
		height: window.innerHeight
	});
	window.addEventListener('resize', updateSize, false);
	window.addEventListener('orientationchange', debounce(updateSize, 350), false);

	let isFirstVisit = true;
	try {
		// See notes below regarding weird format
		// isFirstVisit = localStorage.getItem('isFirstVisit') !== 'false';
		isFirstVisit = !localStorage.getItem('isFirstVisit');
	} catch (e) {}

	Promise.resolve(geoFencing(getGeoFromIPAsync)).then(redirected => {
		if (redirected) {
			return;
		}
		let checkCookies = isFirstVisit && accessNested(appData, 'options.cookiesModal');
		if (!checkCookies) {
			window.preventCookies ??= false;
			Store.emit(actions.PAGE_STATE_LOADED);
		} else {
			Store.emit(actions.SHOW_COOKIES_MODAL, () => Store.emit(actions.PAGE_STATE_LOADED));
		}
	});
});

// TODO: utils
// Using innerHTML with script doesn't execute them. This "fixes" it
// Inspired by https://stackoverflow.com/a/20584396/1336843
function execScripts(node) {
	if (!node || !node.children) return;
	let children = [].slice.call(node.children);
	children.forEach(child => {
		if (child.tagName !== 'SCRIPT') {
			// Not a script? Recurse
			return execScripts(child);
		}
		// Make it executable
		try {
			let copy = document.createElement('script');
			copy.text = child.innerHTML;
			[].forEach.call(child.attributes, attr => {
				copy.setAttribute(attr.name, attr.value);
			});
			// child.parentElement.replaceChild(copy, child);
			// GTM doesn't like being included in a div (the tag helper extension complains)
			// Move all scripts out, directly in the body
			child.parentElement.removeChild(child);
			document.body.appendChild(copy);
		} catch (e) {
			console.error('error while loading custom script', e);
		}
	});
}

let pixelsLoaded = false;

const loadStaticPixels = () => {
	if (pixelsLoaded) return;
	pixelsLoaded = true;
	if (window.STATIC_PIXELS) {
		const pixelsElem = document.createElement('div');
		pixelsElem.innerHTML = window.STATIC_PIXELS;
		document.body.appendChild(pixelsElem);
		execScripts(pixelsElem);
	}
};

Store.on(actions.LOAD_STATIC_PIXELS, loadStaticPixels);

/**
 * Called when a new page is loaded and preliminary checks are done (e.g. cookie or not).
 */
Store.on(actions.PAGE_STATE_LOADED, () => {
	const state = Store.get().toJS();
	const appData = state.appData;

	// Load the pixels straight away if Evidon is not here to ask for user consent (GDPR)
	// Otherwise, pixels will be loaded once window.evidon.priorConsentCallback is called
	let evidonBlock = window.evidon && window.evidon.priorConsentCallback;
	if (state.client.pixelConsent !== false && !evidonBlock && !window.preventCookies) {
		loadStaticPixels();
	}

	const parsedQS = queryString.parse(location.search.slice(1));
	let preventCookies = window.preventCookies;

	if (!appData.options.preventTrackingOnLoad) {
		let showtimesOneTrust = accessNested(appData, 'apis.tracking.oneTrust') || {};
		let boltOneTrust = accessNested(appData, 'options.oneTrustCookieConsent.scripts') || {};
		let useOneTrust = showtimesOneTrust.scriptId || showtimesOneTrust.scripts || boltOneTrust;
		initTracker(state.appData, {
			noCookies: preventCookies,
			oneTrustCategory: useOneTrust && (oneTrust.insightsCategory || 'TARGETING')
		});
	}
	if (!preventCookies) {
		// check if user visits for the first time
		let isFirstVisit = true;
		let regionModalDismissed = false;
		try {
			// A bit counter-intuitive to store firstVisit = false in the local storage
			// Would make more sense IMO to store visited = true, this also avoids issue with falsy values conversion (false / undefined differentiation)
			isFirstVisit = localStorage.getItem('isFirstVisit') !== 'false';
			if (isFirstVisit) {
				localStorage.setItem('isFirstVisit', 'false');
			}

			regionModalDismissed = !!localStorage.getItem('regionModalDismissed');
		} catch (err) {
			// No local storage available
		}
		const user = Store.get().user;

		if (user) {
			user.set({
				isFirstVisit,
				regionModalDismissed
			});
		}
	}

	trackPageView(state.activePage.id, null, true);

	// fix landing url error usually caused by missing trailing slash
	if (parsedQS.err && parsedQS.err === '404' && parsedQS.path) {
		const appBasePath = `/${appData.meta.studio.slug}/${appData.meta.title.slug}/${appData.meta.region.slug}`;
		const missedPath = parsedQS.path.replace(appBasePath, '');
		let missedPageId;
		for (const pageId in appData.pages) {
			const page = appData.pages[pageId];
			if (missedPath === page.path) {
				missedPageId = pageId;
				break;
			}
		}
		if (missedPageId) {
			let page = state.appData.pages[missedPageId];
			// TODO: current base instead of hardcoded showtimes
			if (page.base && page.base !== 'showtimes') {
				if (!parsedQS.from404) {
					location.href = missedPath.substring(1) + '?from404=1';
					return;
				}
			} else {
				route(missedPath.substring(1), missedPageId);
				track({ a: 'found_page_from_404', missedPageId });
				state.activePage = state.appData.pages[missedPageId];
			}
		}
	}

	let showtimesStatus = !!(state.showtimes && state.showtimes.status);

	const redirectTo = (showtimesStatus && state.activePage.redirectOnLoadAfterShowtimes) || state.activePage.redirectOnLoad;
	if (redirectTo) {
		if (redirectTo.match(/^(https?:)?\/\//)) {
			location.href = redirectTo;
		}
		let path = getPath(redirectTo);
		if (path && path[0] === '/') {
			path = path.substring(1);
			path = (getPath(state.activePage).substring(1).match(/\//g) || []).map(() => ('../')).join('') + path;
		}
		history.pushState({ pageId: redirectTo }, '', path);
	}

	// CHECK PAGE VISIBILITY - ONLY LOAD APIS ONCE VISIBLE
	let loaded = false;
	const beforeLoad = Date.now();
	const loadAPI = () => {
		if (loaded) return;
		loaded = true;
		Store.emit(actions.START_API);
		const time = ~~((Date.now() - beforeLoad) / 1000);
		track({ a: 'page_visible', l: time, ni: true });
	};
	let hidden;
	let visibilityChange;
	if (typeof document.hidden !== 'undefined') {
		hidden = 'hidden';
		visibilityChange = 'visibilitychange';
	} else if (typeof document.msHidden !== 'undefined') {
		hidden = 'msHidden';
		visibilityChange = 'msvisibilitychange';
	} else if (typeof document.webkitHidden !== 'undefined') {
		hidden = 'webkitHidden';
		visibilityChange = 'webkitvisibilitychange';
	}
	if (!hidden || !document[hidden]) {
		loadAPI();
	} else {
		// TODO: COPY !
		const inactiveModal = (
			<div>
				<div style={{ fontSize: '.8rem', maxWidth: '25rem', margin: '0 auto', padding: '0 1rem', textAlign: 'center', textTransform: 'none' }}>
					The content of this page has not been loaded yet because you seem to be inactive
				</div>
				<div style={{ textAlign: 'center', margin: '.5rem 0 1rem' }}>
					<button type="button" onClick={() => Store.emit(actions.HIDE_MODAL)}
						style={{ background: 'none', border: '1px solid #ccc', borderRadius: '3px', fontSize: '1rem', lineHeight: '2rem', padding: '0 .5rem', cursor: 'pointer' }}>
						I am here
					</button>
				</div>
			</div>
		);

		Store.emit(actions.SHOW_MODAL, inactiveModal, loadAPI, {
			title: 'Are you here?',
			type: 'inactiveModal'
		});

		document.addEventListener(visibilityChange, () => {
			if (loaded || document[hidden]) return;
			const modal = Store.get().modal;
			if (modal && modal.type === 'inactiveModal') Store.emit(actions.HIDE_MODAL);
			loadAPI();
		}, false);
	}
});

Store.on(actions.START_API, () => {
	let state = Store.get();

	let apiRoot = accessNested(state, 'appData.apis.stdata.root');

	if (!accessNested(state, 'appData.options.disableDynamicData')) {
		fetch(apiRoot+'/app_data/dynamic/'+accessNested(state,'appData.meta.id'), {
			method: 'GET',
			headers: {'Accept': 'application/vnd.api+json', 'Content-Type': 'application/vnd.api+json'}
		}).then(res => res.json()).then(o => {
			if(!o) return;
			delete o.meta;
			let safeString = (str) => JSON.stringify((str || '') + '').slice(1, -1);
			o = JSON.parse(
				JSON.stringify(o)
					.replace(/%IMAGE_ROOT%/g, safeString(state.appData.urls.roots.image))
					.replace(/%VIDEO_ROOT%/g, safeString(state.appData.urls.roots.video))
					.replace(/%IMAGE_REGION_ROOT%/g, safeString(state.appData.urls.roots.imageRegion))
			);
			Store.emit(actions.UPDATE_DYNAMIC_DATA, o);
		}).catch(err => console.log('Error getting dynamic app data', err));
	}

	let opts = accessNested(state, 'appData.options', {});
	let needsRegions = opts.autoBoundingRegions || opts.useRegionSwitcher || opts.useRegionMismatchModal || opts.useAlternateLanguages;
	// Should merge that with dynamicData probably
	let appRegionsUrl = apiRoot+'/app_data/title_regions/'+accessNested(state,'appData.meta.title.slug','');
	let regionPromise;
	let demoLinkedRegions = accessNested(state, 'appData.meta.demoLinkedRegions');
	if (needsRegions && (__IS_DEMO__ || __IS_LOCAL__) && demoLinkedRegions) {
		const currentRegion = accessNested(state, 'appData.meta.region.slug', '');
		const regions       = demoLinkedRegions.split(',').map(r => r.trim().toLowerCase()).filter(r => r)
			.reduce((acc, region) => {
				// if (region === currentRegion) return acc;
				acc[region] = {
					url: window.location.href.replace(`/${currentRegion}/`, `/${region}/`)
				};
				return acc;
			}, {});
		regionPromise = Promise.resolve(regions);
	} else {
		regionPromise = !needsRegions ? Promise.resolve() : fetch(appRegionsUrl, {
			method: 'GET',
			headers: {'Accept': 'application/vnd.api+json', 'Content-Type': 'application/vnd.api+json'}
		})
			.then(res => res.json())
			.catch(err => console.log('Error getting app regions', err))
			.then(regions => {
				if (!regions) regions = {};
				delete regions.meta;
				return regions;
			});
	}

	regionPromise = regionPromise.then(regions => {
		Store.emit(actions.UPDATE_DYNAMIC_DATA, { regions });
	});

	//  No Showtimes Page is found, so will probably not need any of this data
	//  If you do then enable features -> noShowtimesPageForceGeo in Thundr
	if (!accessNested(state, 'appData.pages.showtimes') && !accessNested(state, 'appData.options.noShowtimesPageForceGeo')) {
		return;
	}

	let useClientGeo = (geo, source) => regionPromise.then(() => Store.emit(actions.SET_CLIENT_GEO, new Geo(geo), source));

	let parsedQS = queryString.parse(location.search.slice(1));
	parsedQS = encodeHtmlRecursively(parsedQS);
	// NOTE: All characters <, > are replaced with HTML entities to prevent XSS
	//       this might be too restrictive for us in the future, but for now this is the most
	//       secure solutions

	let forceStartGeo = accessNested(state.appData, 'pages.showtimes.data.forceStartGeo');
	let localStorageGeo;
	try {
		localStorageGeo = JSON.parse(window.localStorage.getItem(state.appData.meta.id + '_lastHouse'));
	} catch(e) {}
	if (forceStartGeo) {
		if (typeof forceStartGeo === 'string') {
			rawSearchLocation(forceStartGeo, state.appData.region).then(data => {
				useClientGeo(data, 'forceStart');
			});
		} else {
			useClientGeo(forceStartGeo, 'forceStart');
		}
	} else {
		let locationResolveChain = Promise.resolve();
		let addLocationOption = fn => {
			locationResolveChain = locationResolveChain.then(value => {
				if (value) {
					return value;
				}
				return typeof fn === 'function' ? fn() : fn;
			});
		};
		if (parsedQS.location) {
			let loc = parsedQS.location;
			if (parsedQS.lat && parsedQS.lon) {
				addLocationOption({
					data: {
						lat: parsedQS.lat,
						lon: parsedQS.lon,
						countryCode: parsedQS.cc || accessNested(state, 'appData.meta.region.slug'),
						city: loc,
					},
					type: 'queryString'
				});
			} else {
				addLocationOption(() => {
					return rawSearchLocation(loc, state.appData.region).then(result => {
						return { data: result, type: 'queryString' };
					}).catch(() => {});
				});
			}
		}
		if (localStorageGeo && typeof localStorageGeo === 'object') {
			addLocationOption({ data: localStorageGeo, type: 'storage' });
		}
		addLocationOption(() => {
			let cancel = false;
			let cancelGeo = () => (cancel = true);
			Store.once(actions.SET_CLIENT_GEO, cancelGeo);
			return getGeoFromIPAsync().then(data => {
				if (cancel) return;

				let countryCode = data.countryCode.toLowerCase();
				return { data: { lat: data.lat, lon: data.lon, countryCode: countryCode, city: data.cityLocal || data.city }, type: 'geoip' };
			});
		});

		locationResolveChain.then(result => {
			if (result) {
				useClientGeo(result.data, result.type);
			}
		}).catch(err => {
			console.log('Error getting geo', err);
			Store.get().showtimes.set({ loading: false, error: 'UNKNOWN' });
		});
	}
});

// Simple helper function to make sure we
// don't fire a pageview track event multiple times on the same page
let lastTrackedPage = '';
function trackPageView(pageId, extraOptions, firstLoad) {
	if (pageId === lastTrackedPage) {
		return;
	}
	extraOptions = Object.assign({ ni: true }, extraOptions);
	track({
		...extraOptions,
		a: pageId + '-view'
	});

	updateDataLayerPageView(pageId, firstLoad);

	if (pageId === 'showtimes') {
		track({ once: true, a: 'start_dimensions', l: document.documentElement.offsetWidth + 'x' + document.documentElement.offsetHeight, ni: 1 });
	} else if (pageId === 'videos') {
		updateDataLayer({ event: 'trailer-view' });
	}

	lastTrackedPage = pageId;

}

// =~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=
// Modal events

/**
 * Show the modal dialog with a custom content.
 * @param {string|JSX} content - the content of the modal
 * @param {Function=} callback - function called when the modal is closed
 * @param {Object=} extra - extra modal property
 */
Store.on(actions.SHOW_MODAL, (content, callback, extra) => {
	const state = Store.get();
	// if (!state.modal) state = state.set({ modal: {} });
	// TODO: Find a way to clear previous content maybe?
	state.set({
		modal: {
			active: true,
			content: content,
			callback: callback,
			...extra
		}
	});
});

/**
 * Close the modal and notify the caller.
 * @param {Object} result -
 * @param {Object} data -
 */
Store.on(actions.HIDE_MODAL, (result, data) => {
	const state = Store.get();
	// if (!state.modal) state = state.set({modal: {}});
	const cb = state.modal && state.modal.callback;
	state.set({
		modal: {
			active: false,
			result: result,
			data: data
		}
	});
	if (typeof cb === 'function') {
		cb(result, data);
	}
});

/**
 * Update the content of the modal.
 * @param {string|JSX} content - the new content
 */
Store.on(actions.UPDATE_MODAL, (content) => {
	const state = Store.get();
	state.modal.set('content', content);
});


// =~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=
// Dynamic data

Store.on(actions.UPDATE_DYNAMIC_DATA, (dynamicData) => {
	// Update the dynamic data
	Store.get().dynamicData.set(dynamicData);
});

/**
 * Register that the region modal was dismissed by the user.
 */
Store.on(actions.SET_USER_REGION_MODAL_DISMISSED, () => {
	const user = Store.get().user;
	// Update localstorage
	localStorage.setItem('regionModalDismissed', true);
	if (user) {
		// Update the store
		user.set({ regionModalDismissed: true });
	}
});


Store.on(actions.SET_CLIENT_GEO, (geo, source, keepCurrentFilters) => {
	// Save the client geo
	if (geo && window.dataLayer && typeof dataLayer.push === 'function') {
		updateDataLayer({ city: geo.city, event: 'city_changed' });
	}

	let state = Store.get();
	state.client.set({ geo, geoSource: source }).now();


	displayRegionModalIfNeeded();
	// let toCapital = () => {
	// 	if(source !== 'geoip') return false;
	// 	let capital = accessNested(state, 'appData.meta.region.capital');
	// 	if(!capital || !capital.lat || !capital.lon || !capital.name) return false;
	// 	Store.emit(actions.SET_CLIENT_GEO, new Geo({lat: capital.lat, lon: capital.lon, city: capital.name, countryCode: state.appData.meta.region.slug}));
	// 	return true;
	// };
	Store.emit(actions.GET_SCREENINGS, geo, keepCurrentFilters);
});

// =~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=
// Misc events

/**
 * Set the current state of an iOS fix?
 * Bound to the "blur" and "focus events.
 * @param {number} state - a height? in pixels
 */
Store.on(actions.SET_IOSFIXEDFIX, (state) => {
	Store.get().set({ iosFixedFix: state });
});


// =~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=~=~~=~=~=
// Tracking events

/**
 * Set the current state of an iOS fix?
 * Bound to the "blur" and "focus events.
 * @param {number} state - a height? in pixels
 */
Store.on(actions.ONETRUST_SHOW, () => oneTrust.displayModal());


Store.on(actions.DISABLE_PIXELS, () => {
	Store.get().client.set({ pixelConsent: false });
});

Store.on(actions.ENABLE_PIXELS, () => {
	Store.get().client.set({ pixelConsent: true });
	loadStaticPixels();
});

/* EXTRA INTEGRATIONS
--------------------------------*/

Store.on(actions.PAGE_STATE_LOADED, () => {
	let appData = Store.get().appData.toJS();
	let mailchimp = accessNested(appData, 'apis.mailchimp');
	if (mailchimp && mailchimp.baseUrl && mailchimp.uuid && mailchimp.lid) {
		let finalObject = {
			baseUrl: mailchimp.baseUrl,
			uuid: mailchimp.uuid,
			lid: mailchimp.lid
		};
		let script = document.createElement('script');
		script.onload = () => {
			try {
				window.require(['mojo/signup-forms/Loader'], L => L.start(finalObject));
			} catch(e) {
				Store.emit(actions.API_ERROR, 'Mailchimp', e);
				console.log('MAILCHIMP ERROR', e);
			}
		};
		script.setAttribute('data-dojo-config', 'usePlainJson: true, isDebug: false');
		script.src = '//downloads.mailchimp.com/js/signup-forms/popup/embed.js';
		document.body.appendChild(script);
	}
});

let videoPlayersCount = 0;
Store.on('VIDEO_PLAYER_SHOW', () => {
	videoPlayersCount++;
	Store.get().client.set({ disableOrientationPrompt: true });
});
Store.on('VIDEO_PLAYER_HIDE', () => {
	videoPlayersCount = Math.max(videoPlayersCount - 1, 0);
	if (!videoPlayersCount) {
		Store.get().client.set({ disableOrientationPrompt: false });
	}
});

Store.on(actions.KALTURA_VIDEO, (bool) => {
	const store = Store.get();
	store.videos.set({isNotKaltura: !bool});
});
